import { User } from 'api/users/types';

const maybeObject = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  return value as Record<string, unknown>;
};

const normalizeUser = (value: unknown): User | null => {
  const objectValue = maybeObject(value);

  if (!objectValue) {
    return null;
  }

  const source = maybeObject(objectValue._source);

  return (source || objectValue) as unknown as User;
};

export const extractUsers = (payload: unknown): User[] => {
  if (Array.isArray(payload)) {
    return payload.map(normalizeUser).filter((user): user is User => !!user);
  }

  const payloadObject = maybeObject(payload);

  if (!payloadObject) {
    return [];
  }

  const usersList = payloadObject.users;

  if (Array.isArray(usersList)) {
    return usersList.map(normalizeUser).filter((user): user is User => !!user);
  }

  const dataList = payloadObject.data;

  if (Array.isArray(dataList)) {
    return dataList.map(normalizeUser).filter((user): user is User => !!user);
  }

  const hitsWrapper = maybeObject(payloadObject.hits);
  const hitsList = hitsWrapper?.hits;

  if (Array.isArray(hitsList)) {
    return hitsList.map(normalizeUser).filter((user): user is User => !!user);
  }

  const searchResultsWrapper = maybeObject(payloadObject.search_results);
  const searchResultsHits = maybeObject(searchResultsWrapper?.hits);
  const searchResultsList = searchResultsHits?.hits;

  if (Array.isArray(searchResultsList)) {
    return searchResultsList.map(normalizeUser).filter((user): user is User => !!user);
  }

  const singleUser = normalizeUser(payloadObject);

  return singleUser ? [singleUser] : [];
};

export const extractUserTotal = (payload: unknown, fallback: number): number => {
  if (Array.isArray(payload)) {
    return payload.length;
  }

  const payloadObject = maybeObject(payload);

  if (!payloadObject) {
    return fallback;
  }

  if (typeof payloadObject.total === 'number') {
    return payloadObject.total;
  }

  if (typeof payloadObject.count === 'number') {
    return payloadObject.count;
  }

  const hitsWrapper = maybeObject(payloadObject.hits);
  const hitsTotalWrapper = maybeObject(hitsWrapper?.total);

  if (typeof hitsTotalWrapper?.value === 'number') {
    return hitsTotalWrapper.value;
  }

  const searchResultsWrapper = maybeObject(payloadObject.search_results);
  const searchResultsHits = maybeObject(searchResultsWrapper?.hits);
  const searchResultsTotal = maybeObject(searchResultsHits?.total);

  if (typeof searchResultsTotal?.value === 'number') {
    return searchResultsTotal.value;
  }

  const usersList = payloadObject.users;

  if (Array.isArray(usersList)) {
    return usersList.length;
  }

  const dataList = payloadObject.data;

  if (Array.isArray(dataList)) {
    return dataList.length;
  }

  return fallback;
};

export const extractSingleUser = (payload: unknown): User | null => {
  const users = extractUsers(payload);

  if (users.length > 0) {
    return users[0];
  }

  return null;
};
